/**
 * API client.
 *
 * Tokens live in memory plus localStorage for the refresh token only. The
 * access token is deliberately *not* persisted: it is short-lived and
 * re-derived from the refresh token on load, which limits what an XSS payload
 * can walk away with to a token the server can revoke.
 */

/**
 * Where the API is.
 *
 * On the web this is a relative path: the same origin serves the app and the
 * API, which keeps cookies, CSP and CORS all trivially correct.
 *
 * The native shell has no such luxury. Its pages are served from
 * `capacitor://localhost`, so a relative path resolves to the app bundle and
 * every request fails before it leaves the device. It needs an absolute origin,
 * baked in at build time.
 *
 * Deliberately not imported from ./native — that module imports this one, and
 * a cycle here would be resolved at load time in whichever order the bundler
 * happened to choose.
 */
const NATIVE_API_ORIGIN = import.meta.env.VITE_API_ORIGIN ?? 'https://fintrack.today';

function inNativeShell(): boolean {
  return (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } })
    .Capacitor?.isNativePlatform?.() === true;
}

export const API_ORIGIN = inNativeShell() ? NATIVE_API_ORIGIN : window.location.origin;

const BASE = `${inNativeShell() ? NATIVE_API_ORIGIN : ''}/api/v1`;
const REFRESH_KEY = 'fintrack.refresh';

let accessToken: string | null = null;
let refreshPromise: Promise<boolean> | null = null;

export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string, public details?: unknown) {
    super(message);
  }
}

export function getRefreshToken(): string | null {
  try { return localStorage.getItem(REFRESH_KEY); } catch { return null; }
}

export function setTokens(access: string | null, refresh?: string | null): void {
  accessToken = access;
  try {
    if (refresh === null) localStorage.removeItem(REFRESH_KEY);
    else if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  } catch { /* private browsing */ }

  // Clearing both tokens is the one thing every sign-out has in common —
  // pressing the button, the password fallback on the lock screen, a deleted
  // account, and a session that simply expired. Hooking it here rather than at
  // the four call sites is what makes the involuntary ones behave like the
  // deliberate one.
  //
  // Imported dynamically because ./native imports this module; a static import
  // would be a cycle resolved in whatever order the bundler happened to pick.
  if (access === null && refresh === null) {
    void import('./native').then((n) => n.clearNativeState()).catch(() => {});
  }
}

export function hasSession(): boolean {
  return Boolean(accessToken || getRefreshToken());
}

async function refreshSession(): Promise<boolean> {
  const token = getRefreshToken();
  if (!token) return false;

  // Concurrent 401s must trigger exactly one refresh, not a stampede.
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(`${BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: token }),
        });
        if (!res.ok) { setTokens(null, null); return false; }
        const data = await res.json();
        setTokens(data.accessToken, data.refreshToken);
        return true;
      } catch {
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Set false to skip the automatic refresh-and-retry (used by auth calls). */
  retryOnAuthFailure?: boolean;
}

export async function request<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, retryOnAuthFailure = true } = opts;

  const send = async (): Promise<Response> =>
    fetch(BASE + path, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

  let res: Response;
  try {
    res = await send();
  } catch {
    throw new ApiError(0, 'Could not reach the server. Check your connection.', 'network_error');
  }

  if (res.status === 401 && retryOnAuthFailure) {
    if (await refreshSession()) {
      res = await send();
    }
  }

  if (res.status === 204) return undefined as T;

  let data: unknown;
  try {
    data = await res.json();
  } catch {
    if (res.ok) return undefined as T;
    throw new ApiError(res.status, 'The server sent an unreadable response.');
  }

  if (!res.ok) {
    const err = (data as { error?: { message?: string; code?: string; details?: unknown } }).error;
    throw new ApiError(res.status, err?.message ?? 'Something went wrong', err?.code, err?.details);
  }

  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body ?? {} }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body: body ?? {} }),
};

// ── Auth helpers ─────────────────────────────────────────────────────────────

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: { id: string; email: string; onboardingComplete?: boolean };
}

export async function login(email: string, password: string): Promise<AuthResponse> {
  const data = await request<AuthResponse>('/auth/login', {
    method: 'POST', body: { email, password }, retryOnAuthFailure: false,
  });
  setTokens(data.accessToken, data.refreshToken);
  return data;
}

export async function register(input: {
  email: string; password: string; name?: string; country?: string; currency?: string; locale?: string;
}): Promise<AuthResponse> {
  const data = await request<AuthResponse>('/auth/register', {
    method: 'POST', body: input, retryOnAuthFailure: false,
  });
  setTokens(data.accessToken, data.refreshToken);
  return data;
}

export async function logout(): Promise<void> {
  try { await api.post('/auth/logout'); } catch { /* already invalid */ }
  setTokens(null, null);
}

/** Restores a session on page load. Returns false when there is none. */
export async function restoreSession(): Promise<boolean> {
  if (accessToken) return true;
  return refreshSession();
}

// ── Domain types shared with the API ─────────────────────────────────────────

export interface Me {
  id: string;
  email: string;
  name: string | null;
  country: string;
  currency: string;
  locale: 'en' | 'ar';
  tzOffsetMinutes: number;
  monthlyIncomeMinor: number | null;
  /**
   * The last salary the app saw, offered when no income has been stated. Null
   * once a figure is declared — the declared one is then the answer.
   */
  detectedIncome: { amountMinor: number; occurredAt: string; labelled: boolean } | null;
  plan: string;
  role: string;
  onboardingComplete: boolean;
  aiConsent: boolean;
  /** The phone set up at onboarding. Null for accounts created before it was asked. */
  devicePlatform: 'ios' | 'android' | null;
  stats: { transactions: number; accounts: number };
}

export interface Transaction {
  id: string;
  amountMinor: number;
  currency: string;
  transactionType: 'expense' | 'income' | 'transfer' | 'refund' | 'fee';
  merchantRaw: string | null;
  merchantNormalized: string | null;
  merchantDisplay: string | null;
  description: string | null;
  category: string | null;
  subcategory: string | null;
  categoryConfidence: number | null;
  categoryMethod: string | null;
  categoryReason: string | null;
  isAiCategorized: boolean;
  userCorrected: boolean;
  occurredAt: string;
  paymentMethod: string | null;
  cardLast4: string | null;
  accountName: string | null;
  source: string;
  status: string;
  needsReviewReason: string | null;
  /** Which way a transfer moved the money. Null when the message did not say. */
  transferDirection?: 'out' | 'in' | null;
  /** The user's answer on whether a transfer counts. Null means unanswered. */
  transferCountsAs?: 'expense' | 'income' | 'ignored' | null;
  /** Last four of the other side's account, when the transfer names no person. */
  transferCounterpartyRef?: string | null;
  parseConfidence: number | null;
  /** Basis points of this charge that are the user's. 10000 = all of it. */
  shareBps: number;
  effectiveAmountMinor: number;
  notes: string | null;
}

export interface CategoryTotal {
  category: string;
  totalMinor: number;
  count: number;
  sharePct: number;
}

export interface SubscriptionEntry {
  merchant: string;
  merchantDisplay: string;
  subcategory: string | null;
  amountMinor: number;
  currency: string;
  lastChargedAt: string;
  charges: number;
  /** Only a confirmed subscription has a rhythm, and so a next date. */
  frequency: string | null;
  nextExpected: string | null;
  confidence: number | null;
}

export interface Subscriptions {
  currency: string;
  confirmed: SubscriptionEntry[];
  unconfirmed: SubscriptionEntry[];
  monthlyMinor: number;
}

export interface SafeToSpend {
  cycle: {
    from: string; to: string; anchored: boolean; remainingDays: number; elapsedDays: number;
    snapped?: boolean;
  };
  currency: string;
  incomeMinor: number;
  spentMinor: number;
  committedMinor: number;
  /** Standing costs the bank never messages about — rent, fees, and the rest. */
  plannedMinor: number;
  /** Claimed by the savings target before anything is spendable. */
  savingsMinor: number;
  availableMinor: number;
  dailyAllowanceMinor: number;
  spentTodayMinor: number;
  /** The transactions spentTodayMinor is made of, so the total can be checked. */
  todayItems: {
    id: string;
    merchant: string;
    amountMinor: number;
    currency: string;
    convertedMinor: number;
    category: string | null;
    occurredAt: string;
  }[];
  basis: 'income_this_cycle' | 'declared_monthly_income' | 'insufficient_data';
  assumptions: string[];
  /** The same sentences in Arabic, paired by index. */
  assumptionsAr: string[];
  /**
   * A credit the app took for a salary and restarted the cycle on, without
   * being sure. Null once answered, either way.
   */
  salaryQuestion: {
    transactionId: string;
    merchant: string | null;
    amountMinor: number;
    occurredAt: string;
  } | null;
}

export interface PlannedExpense {
  id: string;
  name: string;
  amountMinor: number;
  currency: string;
  category: string | null;
  dueDay: number | null;
  appearsInTransactions: boolean;
  note: string | null;
  outstandingMinor: number;
  /** Already out and already in the spending totals, so not subtracted again. */
  settled: boolean;
}

export interface PlannedExpenses {
  currency: string;
  totalMinor: number;
  outstandingMinor: number;
  items: PlannedExpense[];
}

export interface SavingsTarget {
  amountMinor: number | null;
  pct: number | null;
}

export interface UpcomingCharge {
  id: string;
  merchant: string;
  merchantDisplay: string | null;
  category: string | null;
  amountMinor: number;
  currency: string;
  dueOn: string;
  daysAway: number;
  frequency: string;
  isSubscription: boolean;
  varies: boolean;
  /** Progress through a buy-now-pay-later plan, when the charge is one. */
  installment: { paid: number; total: number } | null;
  confidence: number;
  /** True when the amount or date shown is the user's correction, not a guess. */
  edited: boolean;
}

export interface Upcoming {
  currency: string;
  charges: UpcomingCharge[];
  totalMinor: number;
  beforePaydayMinor: number;
  windowDays: number;
}

export interface CashWallet {
  currency: string;
  withdrawnMinor: number;
  spentMinor: number;
  /** Null when no withdrawal has been detected — there is nothing to account for. */
  remainingMinor: number | null;
  unallocatedMinor: number;
  withdrawals: {
    id: string; amountMinor: number; currency: string; occurredAt: string;
    merchant: string | null; allocatedMinor: number; remainingMinor: number;
  }[];
  entries: {
    id: string; amountMinor: number; currency: string; category: string | null;
    subcategory: string | null; merchant: string | null; note: string | null;
    occurredAt: string; withdrawalId: string | null;
  }[];
}

export interface ExpectedRefund {
  id: string;
  transactionId: string;
  /** 'merchant' is a return owed by a shop; 'person' is a share of a split. */
  kind: 'merchant' | 'person';
  owedBy: string | null;
  merchant: string | null;
  amountMinor: number;
  currency: string;
  expectedBy: string;
  daysOverdue: number;
  note: string | null;
  purchasedAt: string;
  settledAt: string | null;
}

export interface Wrap {
  cycle: { from: string; to: string; label: string; anchored: boolean };
  currency: string;
  spentMinor: number;
  incomeMinor: number;
  savedMinor: number;
  savedPct: number | null;
  transactionCount: number;
  topCategories: { category: string; totalMinor: number; sharePct: number }[];
  topMerchants: { merchant: string; display: string | null; totalMinor: number; count: number }[];
  busiestDay: { day: string; totalMinor: number } | null;
  previousSpentMinor: number | null;
  changePct: number | null;
  subscriptionsMinor: number;
  headline: { en: string; ar: string } | null;
}

export interface Dashboard {
  period: { key: string; label: string; from: string; to: string };
  currency: string;
  summary: {
    spentMinor: number; incomeMinor: number; remainingMinor: number; netMinor: number;
    refundsMinor: number; transfersMinor: number; feesMinor: number;
    transactionCount: number; dailyAverageMinor: number;
  };
  previousSummary: { spentMinor: number; incomeMinor: number; transactionCount: number };
  byCategory: CategoryTotal[];
  categoryComparison: { category: string; currentMinor: number; previousMinor: number; changePct: number | null; changeMinor: number }[];
  topMerchants: { merchantNormalized: string; totalMinor: number; count: number; lastAt: string }[];
  dailyTrend: { key: string; label: string; spentMinor: number; incomeMinor: number }[];
  previousDailyTrend: { key: string; label: string; spentMinor: number; incomeMinor: number }[];
  budgets: BudgetProgress[];
}

export interface BudgetProgress {
  id: string; category: string; budgetMinor: number; spentMinor: number;
  remainingMinor: number; progressPct: number; status: 'ok' | 'warning' | 'exceeded'; period: string;
  /** The window the figures cover, so no screen has to infer it. */
  windowFrom: string; windowTo: string; daysRemaining: number;
}

export interface Insight {
  id: string; kind: string; severity: 'info' | 'positive' | 'warning';
  title: string; body: string; titleAr: string; bodyAr: string;
  evidence: Record<string, unknown>;
  provenance: 'confirmed' | 'estimated';
}

export interface SubscriptionSummary {
  currency: string;
  subscriptions: {
    merchant: string; merchantDisplay: string; category: string | null; amountMinor: number; frequency: string;
    lastCharge: string; nextExpected: string; monthlyEquivalentMinor: number;
    estimatedAnnualMinor: number; confidence: number; transactionIds: string[];
  }[];
  totals: { count: number; monthlyMinor: number; annualMinor: number; provenance: string };
  allRecurring: {
    merchant: string; merchantDisplay: string; category: string | null;
    amountMinor: number; frequency: string; isSubscription: boolean;
    monthlyEquivalentMinor: number; nextExpected: string; confidence: number;
  }[];
  commitments: { monthlyMinor: number; annualMinor: number };
}

export interface Affordability {
  currency: string;
  amountMinor: number;
  verdict: 'likely_affordable' | 'tight' | 'likely_unaffordable' | 'insufficient_data';
  figures: {
    monthlyIncomeMinor: number | null;
    monthlyIncomeProvenance: string;
    typicalMonthlySpendMinor: number | null;
    spentThisMonthMinor: number;
    upcomingRecurringMinor: number;
    projectedSurplusMinor: number | null;
    surplusAfterPurchaseMinor: number | null;
    knownBalanceMinor: number | null;
    daysRemainingInMonth: number;
  };
  assumptions: string[];
  /** The same sentences in Arabic, paired by index. */
  assumptionsAr: string[];
  limitations: string[];
  limitationsAr: string[];
  historyMonths: number;
  disclaimer: string;
}

export interface AssistantAnswer {
  conversationId: string;
  answer: string;
  toolCalls: { name: string; input: unknown }[];
  supportingTransactions: {
    id: string; amountMinor: number; currency: string; merchantRaw: string | null;
    merchantNormalized: string | null; category: string | null; occurredAt: string; transactionType: string;
  }[];
  model: string;
  usedFallback: boolean;
}
